import type { AxiosInstance, AxiosError } from "axios"

import type { LoginResponse, RefreshTokenResponse, RegisterResponse, UserDetailsResponse, LoginCredentials } from '@/types/auth.types'

import AppService from "./app.service"

import { NavigationService } from "@/providers/NavigationProvider"
import { AUTH, USER } from "@/config/api.endpoints"

export const createAuthService = (client: AxiosInstance) => {
  return {
    register: async (username: string, email: string, password: string): Promise<RegisterResponse> => {
      const { data } = await client.post(AUTH.REGISTER, {
        username,
        email,
        password,
      })
      return data
    },
    login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
      const { data } = await client.post(AUTH.LOGIN, {
        ...credentials,
      })
      return data
    },
    logout: async (): Promise<void> => {
      await client.post(AUTH.LOGOUT)
    },
    getUserDetails: async (): Promise<UserDetailsResponse> => {
      const { data } = await client.get(USER.ME)
      return data
    },
    refreshToken: async (): Promise<RefreshTokenResponse> => {
      const { data } = await client.post(AUTH.REFRESH)
      return data
    }
  }
}
  // eventBus.emit("user:logout", "refresh:signout")
  // TODO: [MVP] .then() handle error (clear localstorage or trigger logout via eventbus)


/*
const axiosDefaults: CreateAxiosDefaults = {
  withCredentials: true,
  baseURL: AppService.getEnv() === 'production' ? AppService.getApiBaseURL() : '',
  timeout: AppService.getApiTimeout(),
  headers: {
    "ngrok-skip-browser-warning": "none",
    "Content-Type": "application/json",
    Accept: "application/json",
  },
}

const axiosNoAuthInstance = axios.create(axiosDefaults)
const axiosInstance = axios.create(axiosDefaults)

axiosInstance.interceptors.request.use(
  (config) => {
    const token = AuthService.getLocalAccessToken()
    if (token !== null && token !== undefined) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export interface ApiError {
  message: string
  status: number
}
*/
NavigationService.setBaseUrl(AppService.getAppBaseURL() || "")

const handleRedirect = (err: AxiosError): Promise<any> => {
  if (err.response?.status === 302) {
    const location = err.response.headers.location

    if (location) {
      const navigationState = err.response.headers["x-navigation-state"]
      const replace = err.response.headers["x-navigation-replace"] === "true"

      let state = undefined
      if (navigationState) {
        try {
          state = JSON.parse(navigationState)
        } catch (e) {
          console.warn("Failed to parse navigation state:", e)
        }
      }

      const options = {
        replace,
        state,
      }

      const path = location

      setTimeout(() => NavigationService.navigate(path, options), 0)
      return Promise.resolve({
        data: {
          redirected: true,
          to: path,
          options,
        },
      })
    }
  }

  return Promise.reject(err)
}
/*
axiosNoAuthInstance.interceptors.response.use(
  (res) =>{
    if (res.data?.redirectTo) {
      NavigationService.navigate(res.data.redirectTo)
    }
    return res
  },
  handleRedirect
)

axiosInstance.interceptors.response.use(
  (res) =>{
    if (res.data?.redirectTo) {
      NavigationService.navigate(res.data.redirectTo)
    }
    return res
  },
  async (err) => {
    if (err.response && err.response.data?.redirectTo) {
      NavigationService.navigate(err.response.data.redirectTo)
    }

    if (err.response && err.response.status === 302) {
      return handleRedirect(err)
    }

    const originalConfig = err.config

    if (
      err.response &&
      err.response.status === 401 &&
      !originalConfig._retry &&
      AuthService.hasRefreshToken()
    ) {
      originalConfig._retry = true

      try {
        console.log("Refreshing access token")
        localStorage.removeItem("accessToken")
        // TODO: Handle error from refreshToken()
        const { accessToken } = await AuthService.refreshToken()
        localStorage.setItem("accessToken", accessToken)
        originalConfig.headers.Authorization = accessToken
          ? `Bearer ${accessToken}`
          : ""

        return axiosInstance(originalConfig)
      } catch (_error) {
        if (axios.isAxiosError(_error)) {
          const axiosError = _error as AxiosError<ApiError>
          return Promise.reject(
            new Error(axiosError.response?.data.message ?? "Unknown error")
          )
        }

        return Promise.reject(_error)
      }
    }

    if (err.response && err.response.status === 403 && err.response.data) {
      return Promise.reject(err.response.data)
    }

    return Promise.reject(err)
  }
)

const ApiService = {
  auth: {
    register,
    login,
    logout,
    authCheck,
    getUserDetails,
    refreshToken,
  },
  NavigationService,
  setGlobalRedirectHandler: (handler: (url: string) => void) => {
    NavigationService.setGlobalRedirectHandler(handler)
  },
}
*/

export type AuthService = ReturnType<typeof createAuthService>;
